/**
 * Typed REST client für den TCM Business Service (v3).
 *
 * Pfade sind relativ — in dev proxyt Vite auf localhost:8080, in prod
 * liefert der Go-Server SPA + API aus demselben Origin.
 */
import { accessToken, login } from "./auth";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  const tok = accessToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    // Token abgelaufen/fehlt → neu einloggen (Redirect).
    void login();
  }
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text().catch(() => "");
    }
    const msg =
      detail && typeof detail === "object" && "error" in detail
        ? String((detail as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, detail);
  }
  const ct = res.headers.get("content-type") ?? "";
  return ct.includes("application/json") ? ((await res.json()) as T) : (undefined as T);
}

function putJSON<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ---------- Shapes ----------

export type IdP = "entra" | "moodle_local";

export interface Lieferart {
  edu: string;
  name?: string;
  identity_provider: IdP;
  kurs_vorlauf_tage: number;
  teilnehmer_vorlauf_tage: number;
  trainer_vorlauf_tage: number;
  fruehestes_start: string; // YYYY-MM-DD
}

export interface WelcomeMail {
  subject: string;
  body: string;
}

export interface JobStatus {
  running: boolean;
  kind?: "full" | "delta";
  started_at?: string;
}

export interface SystemStatus {
  name: string;
  ok: boolean;
  detail: string;
}

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  seq: number;
  time: string;
  level: LogLevel;
  line: string;
}

// ---------- Endpoints ----------

export const api = {
  getLieferarten: () =>
    request<{ lieferarten: Lieferart[] }>("/api/v2/lieferarten").then((r) => r.lieferarten ?? []),
  putLieferarten: (lieferarten: Lieferart[]) =>
    putJSON<{ lieferarten: Lieferart[] }>("/api/v2/lieferarten", { lieferarten }).then(
      (r) => r.lieferarten ?? [],
    ),

  getWelcomeMail: () => request<WelcomeMail>("/api/v2/welcome-mail"),
  putWelcomeMail: (t: WelcomeMail) => putJSON<WelcomeMail>("/api/v2/welcome-mail", t),

  fullSync: () => request<{ status: string }>("/api/v2/full-sync", { method: "POST" }),
  getStatus: () => request<JobStatus>("/api/v2/status"),
  getSystems: () =>
    request<{ systems: SystemStatus[] }>("/api/v2/systems").then((r) => r.systems ?? []),
};

export const apiV1 = {
  health: () => request<{ status: string; time: string }>("/api/v1/health"),
  getLogs: (since: number, max = 200) =>
    request<{ entries: LogEntry[] }>(`/api/v1/logs?since=${since}&max=${max}`),
};
